import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  Box,
  Autocomplete,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridColDef } from "@mui/x-data-grid";
import MUITable from "./MUITable";
import {
  getProjectName,
  getBiAutomationData,
  deleteBiRecords,
  biDownloadAsset,
  processBi,
} from "../services/BackendServices";
import "./Styles.css";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../redux/Hook";
import {
  ASSET_DOWNLOAD_FAILED,
  DELETE,
  DELETE_BI_DATA,
  ERROR_BI_AUTOMATION,
  ERROR_PROCESS_BI,
  FAILED_BI_AUTOMATION,
  UNEXPECTED_BI_ERROR,
  UNKNOWN_ERROR,
} from "../constants/constants";
import { HTMLComponent } from "../components/HTMLComponent";

const BiAutomation = () => {
  const [open, setOpen] = useState(false);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [projectOptions, setProjectOptions] = useState<string[]>([]);
  const [fileMap, setFileMap] = useState<{ qlik?: File; table?: File }>({});
  const [uploads, setUploads] = useState<
    Array<{
      id: number;
      asset_id: string;
      project_name: string;
      creation_date: string;
      created_by: string;
    }>
  >([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);

  const notification = useAppSelector((state) => state?.NotificationAlert);
  const dispatch = useDispatch();
  const fetchdata = async () => {
    await fetchProjectNames();
    await fetchBiAutomationData();
  };

  useEffect(() => {
    fetchdata();
  }, []);

  const fetchProjectNames = async () => {
    const response = await getProjectName();
    if (response?.success) {
      setProjectOptions(response?.data?.project_names);
    } else {
      setProjectOptions([]);
    }
  };

  const fetchBiAutomationData = async () => {
    try {
      const response = await getBiAutomationData();
      if (response?.success && response?.data?.mongo_bi_assets) {
        const mappedData = response?.data?.mongo_bi_assets?.map(
          (
            item: {
              asset_id: any;
              project_name: any;
              creation_date: any;
              created_by: any;
            },
            index: number
          ) => ({
            id: index + 1,
            asset_id: item?.asset_id,
            project_name: item?.project_name,
            creation_date: item?.creation_date,
            created_by: item?.created_by,
          })
        );
        setUploads(mappedData);
      } else {
        console.error(FAILED_BI_AUTOMATION, response?.message);
      }
    } catch (error) {
      console.error(ERROR_BI_AUTOMATION, error);
    }
  };

  const handleDownloadAsset = async (assetId: string) => {
    setLoading(true);
    try {
      const response = await biDownloadAsset(assetId);
      if (response?.success && response?.data) {
        const blobUrl = URL.createObjectURL(response?.data);
        const downloadLink = document?.createElement("a");
        downloadLink.href = blobUrl;
        downloadLink.download = `${assetId}`;

        document.body.appendChild(downloadLink);
        downloadLink.click();

        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 100);
      } else {
        dispatch({
          type: "SEND_ALERT",
          data: {
            ...notification,
            enable: true,
            type: "error",
            message: response?.data?.message ?? response?.message,
            duration: 3000,
          },
        });
      }
    } catch (error) {
      dispatch({
        type: "SEND_ALERT",
        data: {
          ...notification,
          enable: true,
          type: "error",
          message: ASSET_DOWNLOAD_FAILED,
          duration: 3000,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const uploadToDelete = uploads?.find((upload) => upload?.id === id);
    if (!uploadToDelete) return;

    const response = await deleteBiRecords(uploadToDelete?.asset_id);
    if (response?.success) {
      setUploads(uploads?.filter((upload) => upload?.id !== id));
    } else {
      console.error(DELETE_BI_DATA, response?.error);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleMenuItemClick = (action: string) => {
    if (selectedId !== null) {
      if (action === DELETE) {
        handleDelete(selectedId);
      }
    }
    handleMenuClose();
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    if (event?.target?.files && event?.target?.files?.length > 0) {
      setFileMap((prev) => ({
        ...prev,
        [type]: event?.target?.files![0],
      }));
    }
  };

  const handleSubmit = async () => {
    if (!projectName || !fileMap?.qlik) {
      return;
    }

    const formData = new FormData();
    formData?.append("project_name", projectName);
    formData?.append("image", fileMap?.qlik);
    setLoading(true);
    try {
      const response = await processBi(formData);
      if (response?.success) {
        fetchBiAutomationData();
        setOpen(false);
      } else {
        console.error(ERROR_PROCESS_BI, response?.message || UNKNOWN_ERROR);
      }
    } catch (error) {
      console.error(UNEXPECTED_BI_ERROR, error);
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "asset_id",
      headerName: "Validation Asset",
      flex: 2,
      renderCell: (params) => (
        <div
          className="asset-name-cursor-blue"
          onClick={() => handleDownloadAsset(params?.row?.asset_id)}
        >
          {params?.row?.asset_id}
        </div>
      ),
    },
    { field: "project_name", headerName: "Project Name", flex: 2 },
    { field: "creation_date", headerName: "Creation Date", flex: 1 },
    { field: "created_by", headerName: "Created By", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={(event) => handleMenuOpen(event, params?.row?.id)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Paper className="bi-automation-main">
      <Box className="bi-automation-add">
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          endIcon={<AddIcon />}
        >
          Add File
        </Button>
      </Box>

      <MUITable
        rows={uploads}
        columns={columns}
        tableHeight={450}
        loading={loading}
        defaultPageSize={10}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleMenuItemClick(DELETE)}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload BI Automation Data</DialogTitle>
        <DialogContent>
          {loading && <CircularProgress />}
          <FormControl fullWidth margin="normal">
            <Autocomplete
              options={projectOptions}
              getOptionLabel={(option) => option}
              value={projectName}
              onChange={(_, newValue) => setProjectName(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Project"
                  variant="outlined"
                />
              )}
            />
          </FormControl>

          <Box className="bi-automation-upload">
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Upload File
              <input
                type="file"
                hidden
                onChange={(e) => handleFileChange(e, "qlik")}
              />
            </Button>
            <Typography>{fileMap?.qlik?.name}</Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BiAutomation;
